import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthContext, type AuthResult, type AuthState } from './AuthContext'
import { getProfile } from '../lib/api'
import { demoDb, DEMO_PASSWORD, IDS } from '../lib/mock/data'
import { normalizePhone, toAuthEmail } from '../lib/phone'
import { isSupabaseConfigured, supabase } from '../lib/supabase'
import type { Profile } from '../types/db'

const DEMO_SESSION_KEY = 'lafolie-demo-session'

/** Where Sign out lands. A clean entry: no `from`, so the next person to sign in on this phone is not sent to the previous user's page. */
const SIGNED_OUT_PATH = '/login'

/** Accounts created by Michel start with a temporary password. */
const MUST_CHANGE_KEY = 'must_change_password'

/** In the demo dataset the newest candidate still has a temporary password. */
const DEMO_MUST_CHANGE_IDS = new Set<string>([IDS.hana])

const SIGN_IN_FAILED =
  'That phone number and password did not match. Check the number, then try again. If you are stuck, message Michel on WhatsApp.'

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const [signedOutByUser, setSignedOutByUser] = useState(false)
  const [status, setStatus] = useState<AuthState['status']>('loading')
  const [userId, setUserId] = useState<string | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [mustChangePassword, setMustChangePassword] = useState(false)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const loadProfile = useCallback(async (id: string) => {
    const next = await getProfile(id)
    if (!mounted.current) return
    setProfile(next)
  }, [])

  /* ---------------------------------------------------------------------- */
  /* Session bootstrap                                                       */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    let cancelled = false

    async function bootstrap() {
      if (!isSupabaseConfigured || !supabase) {
        const stored = localStorage.getItem(DEMO_SESSION_KEY)
        const demoProfile = stored ? demoDb.profiles.find((p) => p.id === stored) : undefined
        if (cancelled) return
        if (demoProfile) {
          setUserId(demoProfile.id)
          setProfile(demoProfile)
          setMustChangePassword(false)
          setStatus('signedIn')
        } else {
          setStatus('signedOut')
        }
        return
      }

      const { data } = await supabase.auth.getSession()
      if (cancelled) return
      const session = data.session
      if (session?.user) {
        setUserId(session.user.id)
        setMustChangePassword(session.user.user_metadata?.[MUST_CHANGE_KEY] === true)
        await loadProfile(session.user.id)
        if (!cancelled) setStatus('signedIn')
      } else {
        setStatus('signedOut')
      }
    }

    void bootstrap()

    if (!isSupabaseConfigured || !supabase) return () => { cancelled = true }

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return
      if (session?.user) {
        setUserId(session.user.id)
        setMustChangePassword(session.user.user_metadata?.[MUST_CHANGE_KEY] === true)
        void loadProfile(session.user.id).then(() => {
          if (!cancelled) setStatus('signedIn')
        })
      } else {
        setUserId(null)
        setProfile(null)
        setMustChangePassword(false)
        setStatus('signedOut')
      }
    })

    return () => {
      cancelled = true
      listener.subscription.unsubscribe()
    }
  }, [loadProfile])

  /* ---------------------------------------------------------------------- */
  /* Actions                                                                 */
  /* ---------------------------------------------------------------------- */

  const signIn = useCallback(async (phone: string, password: string): Promise<AuthResult> => {
    setSignedOutByUser(false)
    const email = toAuthEmail(phone)
    if (!email) {
      return { ok: false, error: 'That does not look like a mobile number.' }
    }

    if (!isSupabaseConfigured || !supabase) {
      const normalized = normalizePhone(phone)
      const match = demoDb.profiles.find((p) => normalizePhone(p.phone) === normalized)
      if (!match || password !== DEMO_PASSWORD) {
        return { ok: false, error: SIGN_IN_FAILED }
      }
      if (match.status === 'departed' || match.status === 'inactive') {
        return {
          ok: false,
          error: 'This account is no longer active. Contact Michel if that is wrong.',
        }
      }
      localStorage.setItem(DEMO_SESSION_KEY, match.id)
      setUserId(match.id)
      setProfile(match)
      setMustChangePassword(DEMO_MUST_CHANGE_IDS.has(match.id))
      setStatus('signedIn')
      return { ok: true }
    }

    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      // A 400 really is a wrong phone or password. Anything else is the backend
      // being unreachable or not set up, and telling a coach their password is
      // wrong in that case sends them chasing the wrong problem.
      const status = (error as { status?: number }).status
      if (status && status !== 400 && status !== 401) {
        return {
          ok: false,
          error:
            'The portal could not reach the server. Check your connection and try again. If it keeps failing, tell Michel.',
        }
      }
      // Supabase returns a deliberately vague message. Ours is friendlier and
      // still says nothing about whether the account exists.
      return { ok: false, error: SIGN_IN_FAILED }
    }
    if (data.user) {
      setUserId(data.user.id)
      setMustChangePassword(data.user.user_metadata?.[MUST_CHANGE_KEY] === true)
      await loadProfile(data.user.id)
      setStatus('signedIn')
    }
    return { ok: true }
  }, [loadProfile])

  const signOut = useCallback(async () => {
    if (isSupabaseConfigured && supabase) {
      await supabase.auth.signOut()
    }
    localStorage.removeItem(DEMO_SESSION_KEY)
    // Deliberate sign-out, not an expired session. The flag renders in the same
    // batch as the status change, so RequireAuth sees it and does not remember
    // the open screen as `from`: otherwise the next person to sign in on this
    // phone (Gina after a coach, say) would land on that coach's page. The
    // navigate() is the belt to that brace; React Router 7 applies it in a
    // transition, after the redirect, which is why the flag is what decides.
    setSignedOutByUser(true)
    setUserId(null)
    setProfile(null)
    setMustChangePassword(false)
    setStatus('signedOut')
    navigate(SIGNED_OUT_PATH, { replace: true })
  }, [navigate])

  /**
   * First-login password change. Placeholder in the sense that the real flow
   * also needs an "I forgot it" path, which requires either an SMS provider or
   * Michel resetting it by hand. That decision is still open, so for now the
   * screen exists and works for the temporary-password case only.
   */
  const changePassword = useCallback(async (nextPassword: string): Promise<AuthResult> => {
    if (nextPassword.length < 8) {
      return { ok: false, error: 'Use at least 8 characters.' }
    }
    if (!isSupabaseConfigured || !supabase) {
      setMustChangePassword(false)
      return { ok: true }
    }
    const { error } = await supabase.auth.updateUser({
      password: nextPassword,
      data: { [MUST_CHANGE_KEY]: false },
    })
    if (error) return { ok: false, error: error.message }
    setMustChangePassword(false)
    return { ok: true }
  }, [])

  const refreshProfile = useCallback(async () => {
    if (userId) await loadProfile(userId)
  }, [userId, loadProfile])

  const value = useMemo<AuthState>(
    () => ({
      status,
      userId,
      profile,
      role: profile?.role ?? null,
      mustChangePassword,
      demoMode: !isSupabaseConfigured,
      signedOutByUser,
      signIn,
      signOut,
      changePassword,
      refreshProfile,
    }),
    [
      status,
      userId,
      profile,
      mustChangePassword,
      signedOutByUser,
      signIn,
      signOut,
      changePassword,
      refreshProfile,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
